import PropTypes from 'prop-types'
import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Dropdown, FormGroup, FormLabel } from '../../../../../../../components'
import { stringsToDropdownOpt } from '../../../../../../../utils/fns'
import { getUIState } from '../../../../../../../utils/selectors'

import {
  getIJoistFlgThicknessLabel,
  getIJoistFlgThicknessValue,
  getIJoistFlgWidthLabel,
  getIJoistFlgWidthValue,
} from '../../../../../common/fns'

const IJoistSelector = (props) => {
  const { label, manufacturer, series, model, width, onChange } = props

  const manufacturerData = useSelector((state) => getUIState(state).manufacturerData)

  const manufacturerOptions = useMemo(
    () => stringsToDropdownOpt(manufacturerData ? Object.keys(manufacturerData) : []),
    [manufacturerData],
  )
  const seriesOptions = useMemo(
    () => stringsToDropdownOpt(manufacturerData ? Object.keys(manufacturerData[manufacturer]) : []),
    [manufacturerData, manufacturer],
  )

  const modelOptions = useMemo(
    () =>
      stringsToDropdownOpt(
        manufacturerData ? Object.keys(manufacturerData[manufacturer][series]) : [],
      ),
    [manufacturerData, manufacturer, series],
  )

  const flgThicknessLabel = useMemo(() => {
    const selectedModel = manufacturerData[manufacturer][series][model]
    return getIJoistFlgThicknessLabel(selectedModel.material)
  }, [manufacturer, series, model])

  const convertManufacturerData = (data) => {
    const [width, thickness] = [
      getIJoistFlgWidthValue(data.width),
      getIJoistFlgThicknessValue(data.material),
    ]

    return {
      depth: data.depth,
      material: data.material,
      width,
      thickness,
      model: data.product,
    }
  }

  const handleChangeManufacturer = (e) => {
    const { name, value } = e.target
    const selectedSeries = Object.keys(manufacturerData[value])[0]
    const selectedModel = Object.values(manufacturerData[value][selectedSeries])[0]

    onChange({
      [name]: value,
      series: selectedModel.series,
      ...convertManufacturerData(selectedModel),
    })
  }

  const handleChangeSeries = (e) => {
    const { name, value } = e.target
    const selectedModel = Object.values(manufacturerData[manufacturer][value])[0]
    onChange({
      [name]: value,
      ...convertManufacturerData(selectedModel),
    })
  }

  const handleChangeModel = (e) => {
    const selectedModel = manufacturerData[manufacturer][series][e.target.value]
    onChange(convertManufacturerData(selectedModel))
  }

  return (
    <React.Fragment>
      <FormGroup>
        <FormLabel htmlFor={`${label}.manufacturer`}>Manufacturer</FormLabel>
        <Dropdown
          name={`${label}.manufacturer`}
          options={manufacturerOptions}
          value={manufacturer}
          onChange={handleChangeManufacturer}
          data-ga4={`${label}_manufacturer:${manufacturer}`}
          data-testid={`${label}.manufacturer`}
        />
      </FormGroup>

      <FormGroup>
        <FormLabel htmlFor={`${label}.series`}>Series</FormLabel>
        <Dropdown
          name={`${label}.series`}
          options={seriesOptions}
          value={series}
          onChange={handleChangeSeries}
          data-ga4={`${label}_series:${series}`}
          data-testid={`${label}.series`}
        />
      </FormGroup>

      <FormGroup>
        <FormLabel htmlFor={`${label}.model`}>Model</FormLabel>
        <Dropdown
          name={`${label}.model`}
          options={modelOptions}
          value={model}
          onChange={handleChangeModel}
          data-ga4={`${label}_model:${model}`}
          data-testid={`${label}.model`}
        />
      </FormGroup>

      <FormGroup>
        <FormLabel>Flange Thickness</FormLabel>
        <p className="mt-0" data-ga4={`${label}_thickness:${flgThicknessLabel}`}>
          {model && flgThicknessLabel}&nbsp;
        </p>
      </FormGroup>

      <FormGroup>
        <FormLabel>Flange Width</FormLabel>
        <p className="mt-0" data-ga4={`${label}_width:${getIJoistFlgWidthLabel(width)}`}>
          {model && getIJoistFlgWidthLabel(width)}&nbsp;
        </p>
      </FormGroup>
    </React.Fragment>
  )
}

IJoistSelector.propTypes = {
  label: PropTypes.string.isRequired,
  material: PropTypes.number.isRequired,
  manufacturer: PropTypes.string.isRequired,
  series: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
}

export default memo(IJoistSelector)
